// apps/office/src/components/LabourAttendanceModal.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import pb from '../lib/pocketbaseClient';
import { X, Calendar, Users, UserCheck, Clock, RefreshCw, Edit2, Save, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDate } from '../utils/dateHelpers';
import { cn } from '../lib/utils';

export default function LabourAttendanceModal({ labour, open, onClose, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editingStatus, setEditingStatus] = useState({}); // { recordId: newStatus }
  const [updating, setUpdating] = useState(false);

  // Summary stats
  const [stats, setStats] = useState({
    present: 0,
    halfday: 0,
    absent: 0,
    totalDays: 0,
    totalSalary: 0,
  });

  useEffect(() => {
    if (open && labour) {
      // Set default date range: current month
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(lastDay.toISOString().split('T')[0]);
      fetchAttendance();
    }
  }, [open, labour]);

  const fetchAttendance = async () => {
    if (!labour) return;
    setLoading(true);
    try {
      const filter = `labour="${labour.id}"`;
      let dateFilter = '';
      if (dateFrom && dateTo) {
        dateFilter = ` && date >= "${dateFrom}" && date <= "${dateTo}"`;
      }
      const records = await pb.collection('labour_attendance').getFullList({
        filter: filter + dateFilter,
        sort: '-date',
      });
      setAttendanceRecords(records);

      // Calculate stats
      const present = records.filter(r => r.status === 'present').length;
      const halfday = records.filter(r => r.status === 'halfday').length;
      const absent = records.filter(r => r.status === 'absent').length;
      const totalDays = present + (halfday * 0.5);
      const dailyRate = labour.dailyRate || 0;
      const totalSalary = totalDays * dailyRate;

      setStats({ present, halfday, absent, totalDays, totalSalary, dailyRate });
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (recordId, newStatus) => {
    setUpdating(true);
    try {
      await pb.collection('labour_attendance').update(recordId, { status: newStatus });
      toast.success('Attendance updated');
      // Update local state
      setAttendanceRecords(prev =>
        prev.map(r => (r.id === recordId ? { ...r, status: newStatus } : r))
      );
      // Recalculate stats
      const updatedRecords = attendanceRecords.map(r =>
        r.id === recordId ? { ...r, status: newStatus } : r
      );
      const present = updatedRecords.filter(r => r.status === 'present').length;
      const halfday = updatedRecords.filter(r => r.status === 'halfday').length;
      const absent = updatedRecords.filter(r => r.status === 'absent').length;
      const totalDays = present + (halfday * 0.5);
      const dailyRate = labour.dailyRate || 0;
      const totalSalary = totalDays * dailyRate;
      setStats({ present, halfday, absent, totalDays, totalSalary, dailyRate });
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleApplyFilter = () => {
    fetchAttendance();
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) return 'Rs.0.00';
    return `Rs.${parseFloat(amount).toFixed(2)}`;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-500" />
                Attendance History
              </h2>
              <p className="text-sm text-slate-500">
                {labour.name} � {labour.expand?.vendor?.name || 'N/A'} � {labour.expand?.site?.name || 'N/A'}
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-wrap items-center gap-4 bg-slate-50 p-3 rounded-lg mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600">From</span>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-36"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">To</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-36"
              />
            </div>
            <Button onClick={handleApplyFilter} className="bg-amber-500 hover:bg-amber-600 text-white">
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
            <Button variant="outline" onClick={() => { setDateFrom(''); setDateTo(''); fetchAttendance(); }}>
              Reset
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-slate-500">Present</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.present}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-slate-500">Half Day</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.halfday}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-slate-500">Absent</p>
                <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-xs text-slate-500">Total Salary</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(stats.totalSalary)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : attendanceRecords.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No attendance records found</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Check-In</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Check-Out</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {attendanceRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2">{formatDate(record.date)}</td>
                      <td className="px-3 py-2 font-mono text-sm">
                        {record.check_in ? new Date(record.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '�'}
                      </td>
                      <td className="px-3 py-2 font-mono text-sm">
                        {record.check_out ? new Date(record.check_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '�'}
                      </td>
                      <td className="px-3 py-2">
                        <Badge
                          variant={
                            record.status === 'present'
                              ? 'success'
                              : record.status === 'halfday'
                              ? 'warning'
                              : 'secondary'
                          }
                        >
                          {record.status || 'absent'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={record.status || 'absent'}
                          onChange={(e) => handleStatusChange(record.id, e.target.value)}
                          disabled={updating}
                          className="px-2 py-1 border rounded text-sm bg-white focus:ring-2 focus:ring-amber-400"
                        >
                          <option value="present">Present</option>
                          <option value="halfday">Half Day</option>
                          <option value="absent">Absent</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
}